import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { LoadingBlock } from '@/components/Spinner';
import '@/styles/pages.css';

/**
 * Where to land after the session is issued. Kept outside React state because
 * the moment the session exists, App swaps this page for the signed-in tree
 * and `AfterMicrosoftSignIn` (rendered at the same path) reads it instead.
 */
const RETURN_TO_KEY = 'sso.returnTo';

/**
 * Where Microsoft sends the browser back.
 *
 * The callback function outside the VPC has already verified the token and
 * left a one-time ticket in the URL fragment. This page hands that ticket to
 * the API, which decides whether the account may sign in and sets the session
 * cookie — the same cookie every other method sets. The fragment is read once
 * and cleared from the address bar so a bookmark or a screenshot cannot
 * replay it.
 *
 * Failures arrive as `#error=<reason>` from the callback function, or as an
 * API rejection when the ticket is handed in. Both are shown in words a
 * person can act on; nothing here retries on its own, because the most common
 * cause is "this address has no account" and a loop would not fix that.
 */
const REASONS: Record<string, string> = {
  cancelled: 'The Microsoft sign-in was cancelled.',
  state_expired: 'That sign-in took too long. Please start again.',
  unknown_state: 'That sign-in link has already been used or has expired. Please start again.',
  token_rejected: 'Microsoft returned a sign-in this system could not verify. Please try again.',
  exchange_failed: 'Microsoft did not complete the sign-in. Please try again in a moment.',
  misconfigured: 'Microsoft sign-in is not fully set up on this environment. Tell IT.',
  provider_error: 'Microsoft reported a problem with the sign-in.',
  no_identity: 'Microsoft did not tell us which account signed in. Please try again.',
};

export function MicrosoftCallbackPage(): ReactNode {
  const { completeMicrosoft } = useAuth();
  const [problem, setProblem] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // React StrictMode mounts twice in development; a ticket is single-use.
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const ticket = params.get('ticket');
    const error = params.get('error');
    const returnTo = params.get('returnTo') || '/';

    // Clear the fragment before anything else, so it survives in no history
    // entry regardless of how the rest of this goes.
    window.history.replaceState(null, '', window.location.pathname);

    if (error) {
      setProblem(REASONS[error] ?? `Sign-in did not complete (${error}).`);
      return;
    }
    if (!ticket) {
      setProblem('This page is only reached from a Microsoft sign-in.');
      return;
    }

    try {
      sessionStorage.setItem(RETURN_TO_KEY, safePath(returnTo));
    } catch {
      /* private mode: land on the dashboard instead */
    }

    // On success there is nothing to do here: the session exists, App renders
    // the signed-in tree, and AfterMicrosoftSignIn takes over at this path.
    void completeMicrosoft(ticket).catch((err: unknown) => {
      setProblem(err instanceof Error && err.message ? err.message : 'Sign-in did not complete.');
    });
  }, [completeMicrosoft]);

  if (!problem) return <LoadingBlock label="Finishing your Microsoft sign-in…" />;

  return (
    <div className="auth-shell">
      <div className="auth-card auth-card-single">
        <div className="auth-form">
          <h2>Could not sign you in</h2>
          <div className="form-error" role="alert">
            {problem}
          </div>
          <p className="hint">
            If this keeps happening, contact the GDEC Help Desk — accounts are managed by your
            administrator.
          </p>
          <Link className="btn btn-primary" to="/" replace>
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Rendered at /auth/microsoft/callback once signed in: go where the sign-in
 * started from, or to the dashboard. Also what a stale tab lands on.
 */
export function AfterMicrosoftSignIn(): ReactNode {
  // Read in the state initialiser, remove in an effect. Rendering is not the
  // place for the removal: React double-invokes render in development
  // (StrictMode), and the second pass would find nothing and go to the
  // dashboard — which is exactly what happened before this was split.
  const [target] = useState(() => {
    try {
      const stored = sessionStorage.getItem(RETURN_TO_KEY);
      if (stored && stored !== '/') return safePath(stored);
    } catch {
      /* fall through to the dashboard */
    }
    return '/dashboard';
  });
  useEffect(() => {
    try {
      sessionStorage.removeItem(RETURN_TO_KEY);
    } catch {
      /* nothing to remove */
    }
  }, []);
  return <Navigate to={target} replace />;
}

function safePath(value: string): string {
  return value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/auth/') ? value : '/';
}
